import type { GoodsReceipt, ItemVariantAxisOptions } from '../api/masters';
import type { UnitCondition } from '../generated/enums';
import type { IntakeDraft, IntakeLine } from './intake';
import { emptyLine } from './intake';
import { referencesFrom } from './referenceNumber';

/**
 * The phone's Receive stock (`docs/Mobile_Redesign_Plan.md`, step 10): a delivery said the way a
 * storeman counts it off the boxes — a design, its colour, and how many of each size.
 *
 * <p>
 * <strong>Turned into the website's delivery, never posted another way.</strong> Each size with a
 * count becomes one line of `units/intake.ts`'s draft, and that draft's own `toBatchRequest` and
 * `draftProblems` are what reach `POST /api/item-unit/batch` — so the two screens cannot book stock in
 * differently. What differs is only how a person says it.
 * </p>
 *
 * <p>
 * <strong>Size and look are told apart by the garment-code mark, and by being a size</strong>: an
 * axis whose value goes into every garment code (size, or fit where a shop marks it) gets a count
 * per value, and so does Size or Shoe size left out of the code (owner, 2026-10-31 — a shop that
 * numbers garments `JDV-01` still counts them by size). Every other axis (colour, fabric) is one
 * choice at a time.
 * </p>
 *
 * <p>
 * <strong>Several colours of one design</strong> (canvas "Stock in by colour and size", option C,
 * owner 2026-10-31): a line holds the colours already added ({@link ReceiveLine.added}) beside the
 * one being counted (`look` and `counts`). Cost, rent and condition stay one per design.
 * </p>
 */

/** One design in the delivery. */
export interface ReceiveLine {
  /** Stable across removals, unlike an index. Never sent. */
  key: string;
  itemId: number | null;
  /** The look being counted: attributeId → valueId, one per look axis. */
  look: Record<number, number>;
  /** How many of each size of that look, keyed by {@link SizeChoice.key}. */
  counts: Record<string, number>;
  /** The looks already added, each with its own counts — every colour of the design but the open one. */
  added: ReceiveLook[];
  condition: UnitCondition;
  /** Per garment. Null when the storeman does not say. */
  purchaseCost: number | null;
  /**
   * The rent for the whole event (owner, 2026-10-17) — one price however many days it runs, which
   * is how the shops price. Null leaves pricing alone (see `IntakeLine`).
   */
  rentForEvent: number | null;
  /** What each day past the first adds; null charges the event price whatever the length. */
  extraDayRent: number | null;
}

/** One colour (or other look) of a design and how many of each size of it came. */
export interface ReceiveLook {
  look: Record<number, number>;
  counts: Record<string, number>;
}

export interface ReceiveDraft {
  /** The day the delivery came in, `YYYY-MM-DD`. */
  receivedOn: string;
  /** The vendor it came from, marked as a supplier; null records none. */
  supplierVendorId: number | null;
  /** Their invoice number, or anything else worth recording — the delivery's note. */
  note: string;
  lines: ReceiveLine[];
}

/** The most of one size a single delivery line takes — the server's own bound per line. */
export const maxPerSize = 100;

let sequence = 0;

export function receiveLine(itemId: number | null = null): ReceiveLine {
  sequence += 1;

  return {
    key: `receive-${sequence}`,
    itemId,
    look: {},
    counts: {},
    added: [],
    condition: 'New',
    purchaseCost: null,
    rentForEvent: null,
    extraDayRent: null,
  };
}

/** A delivery of one design — the one it was opened for, when it was opened from a design. */
export function emptyReceive(today: string, itemId: number | null = null): ReceiveDraft {
  return { receivedOn: today, supplierVendorId: null, note: '', lines: [receiveLine(itemId)] };
}

export function updateReceiveLine(
  draft: ReceiveDraft,
  key: string,
  changes: Partial<Omit<ReceiveLine, 'key'>>,
): ReceiveDraft {
  return {
    ...draft,
    lines: draft.lines.map((line) => (line.key === key ? { ...line, ...changes } : line)),
  };
}

/** A different design clears the colours and the counts with it — a carried-over colour is wrong. */
export function chooseDesign(draft: ReceiveDraft, key: string, itemId: number): ReceiveDraft {
  return updateReceiveLine(draft, key, { itemId, look: {}, counts: {}, added: [] });
}

export function chooseLook(
  draft: ReceiveDraft,
  key: string,
  attributeId: number,
  valueId: number,
): ReceiveDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);

  return line
    ? updateReceiveLine(draft, key, { look: { ...line.look, [attributeId]: valueId } })
    : draft;
}

/** One more or one fewer of a size, held between none and {@link maxPerSize}. */
export function stepSize(draft: ReceiveDraft, key: string, size: string, by: number): ReceiveDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);

  if (!line) {
    return draft;
  }

  const count = Math.min(maxPerSize, Math.max(0, (line.counts[size] ?? 0) + by));

  return updateReceiveLine(draft, key, { counts: { ...line.counts, [size]: count } });
}

/** One key per look, the same whichever order its axes were chosen in. */
export function lookKey(look: Record<number, number>): string {
  return Object.keys(look)
    .map(Number)
    .sort((a, b) => a - b)
    .map((attributeId) => `${attributeId}:${look[attributeId]}`)
    .join('/');
}

/** How many garments a set of counts holds. */
export function countOf(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, count) => sum + count, 0);
}

/** Counts added into a look's own, never past {@link maxPerSize} for a size. */
function merged(
  into: Record<string, number>,
  more: Record<string, number>,
): Record<string, number> {
  const counts = { ...into };

  for (const [size, count] of Object.entries(more)) {
    counts[size] = Math.min(maxPerSize, (counts[size] ?? 0) + count);
  }

  return counts;
}

/** The looks with one more added; a look added twice is one entry, counted together. */
function withLook(added: readonly ReceiveLook[], entry: ReceiveLook): ReceiveLook[] {
  const key = lookKey(entry.look);

  return added.some((other) => lookKey(other.look) === key)
    ? added.map((other) =>
        lookKey(other.look) === key
          ? { ...other, counts: merged(other.counts, entry.counts) }
          : other,
      )
    : [...added, { look: { ...entry.look }, counts: { ...entry.counts } }];
}

/** The line with what is being counted moved into its added looks, and the counter emptied. */
function committed(line: ReceiveLine): ReceiveLine {
  return countOf(line.counts) === 0
    ? { ...line, counts: {} }
    : { ...line, added: withLook(line.added, line), counts: {} };
}

/** Add: the look being counted joins the list, and the counter starts again on the same look. */
export function addCounted(draft: ReceiveDraft, key: string): ReceiveDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);

  return line ? updateReceiveLine(draft, key, committed(line)) : draft;
}

/**
 * A colour picked on the phone. Switching from a colour already chosen adds what was counted of it
 * first, so moving on never loses a count; filling a look's other axis keeps the counts in hand.
 */
export function pickLook(
  draft: ReceiveDraft,
  key: string,
  attributeId: number,
  valueId: number,
): ReceiveDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);

  if (!line || line.look[attributeId] === valueId) {
    return draft;
  }

  const base = line.look[attributeId] === undefined ? line : committed(line);

  return updateReceiveLine(draft, key, { ...base, look: { ...line.look, [attributeId]: valueId } });
}

/** Change: an added look comes back into the counter, after what was being counted is added. */
export function reopenLook(draft: ReceiveDraft, key: string, look: string): ReceiveDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);
  const base = line ? committed(line) : null;
  const found = base?.added.find((entry) => lookKey(entry.look) === look);

  if (!base || !found) {
    return draft;
  }

  return updateReceiveLine(draft, key, {
    added: base.added.filter((entry) => entry !== found),
    look: { ...found.look },
    counts: { ...found.counts },
  });
}

/** Takes an added look off the list. */
export function removeLook(draft: ReceiveDraft, key: string, look: string): ReceiveDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);

  return line
    ? updateReceiveLine(draft, key, {
        added: line.added.filter((entry) => lookKey(entry.look) !== look),
      })
    : draft;
}

/**
 * The website's Add: so many of each ticked size of one look, straight onto the list (the phone's
 * counter is not used there).
 */
export function addSizes(
  draft: ReceiveDraft,
  key: string,
  look: Record<number, number>,
  sizes: readonly string[],
  quantity: number,
): ReceiveDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);
  const each = Math.min(maxPerSize, Math.max(0, Math.floor(quantity)));

  if (!line || sizes.length === 0 || each === 0) {
    return draft;
  }

  return updateReceiveLine(draft, key, {
    added: withLook(line.added, {
      look,
      counts: Object.fromEntries(sizes.map((size) => [size, each])),
    }),
  });
}

/**
 * One size of an added look set to a count; none takes the size off, and a look left with no size
 * goes with it.
 */
export function setAddedCount(
  draft: ReceiveDraft,
  key: string,
  look: string,
  size: string,
  count: number,
): ReceiveDraft {
  const line = draft.lines.find((candidate) => candidate.key === key);

  if (!line) {
    return draft;
  }

  const held = Math.min(maxPerSize, Math.max(0, Math.floor(count)));
  const added = line.added
    .map((entry) => {
      if (lookKey(entry.look) !== look) {
        return entry;
      }

      const counts = { ...entry.counts };

      if (held === 0) {
        delete counts[size];
      } else {
        counts[size] = held;
      }

      return { ...entry, counts };
    })
    .filter((entry) => countOf(entry.counts) > 0);

  return updateReceiveLine(draft, key, { added });
}

/**
 * Every look the line books in, with its counts: the added ones in the order they were added, then
 * the one in the counter — booked in too, so the last colour needs no Add. A look in both is one
 * entry.
 */
export function countedLooks(line: ReceiveLine): ReceiveLook[] {
  const looks = line.added.filter((entry) => countOf(entry.counts) > 0);

  return countOf(line.counts) > 0 ? withLook(looks, line) : looks;
}

/** "Black", or "Black · Silk" — the values of the look axes, in the axes' order. */
export function lookName(
  look: Record<number, number>,
  axes: readonly ItemVariantAxisOptions[],
): string {
  return splitAxes(axes)
    .looks.map(
      (axis) => axis.values.find((value) => value.valueId === look[axis.attributeId])?.name,
    )
    .filter((name): name is string => name !== undefined)
    .join(' · ');
}

/** The swatch of a look, from the first look axis that has one; null where none does. */
export function lookColour(
  look: Record<number, number>,
  axes: readonly ItemVariantAxisOptions[],
): string | null {
  for (const axis of splitAxes(axes).looks) {
    const hex = axis.values.find((value) => value.valueId === look[axis.attributeId])?.colourHex;

    if (hex) {
      return hex;
    }
  }

  return null;
}

/** "36 × 2 · 40 × 1", in the shop's size order. */
export function countWords(
  counts: Record<string, number>,
  axes: readonly ItemVariantAxisOptions[],
): string {
  return sizeChoices(axes)
    .filter((size) => (counts[size.key] ?? 0) > 0)
    .map((size) => `${size.label} × ${counts[size.key]}`)
    .join(' · ');
}

export function addReceiveLine(draft: ReceiveDraft): ReceiveDraft {
  return { ...draft, lines: [...draft.lines, receiveLine()] };
}

/**
 * A design added to the delivery from its list (the phone's board E1): the line it already has,
 * else the empty line waiting for one, else a new line. Returns the line to open.
 */
export function addDesign(
  draft: ReceiveDraft,
  itemId: number,
): { draft: ReceiveDraft; key: string } {
  const held = draft.lines.find((line) => line.itemId === itemId);

  if (held) {
    return { draft, key: held.key };
  }

  const empty = draft.lines.find((line) => line.itemId === null);

  if (empty) {
    return { draft: chooseDesign(draft, empty.key, itemId), key: empty.key };
  }

  const line = receiveLine(itemId);

  return { draft: { ...draft, lines: [...draft.lines, line] }, key: line.key };
}

/** Removing the last design leaves an empty one: a delivery always has somewhere to start. */
export function removeReceiveLine(draft: ReceiveDraft, key: string): ReceiveDraft {
  const lines = draft.lines.filter((line) => line.key !== key);

  return { ...draft, lines: lines.length > 0 ? lines : [receiveLine()] };
}

/** The attributes counted per value even where a shop leaves them out of the garment code. */
const sizeCodes: ReadonlySet<string> = new Set(['SIZE', 'SHOE_SIZE']);

/** Whether an axis is counted value by value — marked in the code, or a size whatever its mark. */
export function countedBySize(axis: ItemVariantAxisOptions): boolean {
  return axis.inGarmentCode || sizeCodes.has(axis.attributeCode.toUpperCase());
}

/** The axes a design is counted by (sizes) and the ones it is chosen by (the look). */
export function splitAxes(axes: readonly ItemVariantAxisOptions[]): {
  looks: ItemVariantAxisOptions[];
  sizes: ItemVariantAxisOptions[];
} {
  return {
    looks: axes.filter((axis) => !countedBySize(axis)),
    sizes: axes.filter(countedBySize),
  };
}

/** One stepper: a value of every marked axis — "40", or "40 · Slim" where fit is marked too. */
export interface SizeChoice {
  key: string;
  label: string;
  values: Record<number, number>;
}

/**
 * Every size a design can be received in, in the shop's order — the product of its marked axes'
 * values. A design with no marked axis has one choice, "One size", that sets nothing.
 */
export function sizeChoices(axes: readonly ItemVariantAxisOptions[]): SizeChoice[] {
  const marked = splitAxes(axes).sizes;
  let choices: SizeChoice[] = [{ key: '', label: '', values: {} }];

  for (const axis of marked) {
    choices = choices.flatMap((choice) =>
      axis.values.map((value) => ({
        key:
          choice.key === ''
            ? `${axis.attributeId}:${value.valueId}`
            : `${choice.key}/${axis.attributeId}:${value.valueId}`,
        label: choice.label === '' ? value.name : `${choice.label} · ${value.name}`,
        values: { ...choice.values, [axis.attributeId]: value.valueId },
      })),
    );
  }

  return choices.map((choice) => ({ ...choice, label: choice.label || 'One size' }));
}

/** How many garments the line receives — every added look and the one in the counter. */
export function lineCount(line: ReceiveLine): number {
  return countedLooks(line).reduce((sum, entry) => sum + countOf(entry.counts), 0);
}

/** How many garments the whole delivery receives — the figure on the button. */
export function receiveCount(draft: ReceiveDraft): number {
  return draft.lines.reduce((sum, line) => sum + (line.itemId === null ? 0 : lineCount(line)), 0);
}

/**
 * What the footer says about the delivery: how many designs and garments, and what it cost — null
 * while no line gives a cost, rather than a ₹0 nobody paid.
 */
export function receiveSummary(draft: ReceiveDraft): {
  designs: number;
  garments: number;
  cost: number | null;
} {
  const lines = draft.lines.filter((line) => line.itemId !== null && lineCount(line) > 0);
  const costed = lines.filter((line) => line.purchaseCost !== null);

  return {
    designs: new Set(lines.map((line) => line.itemId)).size,
    garments: lines.reduce((sum, line) => sum + lineCount(line), 0),
    cost:
      costed.length === 0
        ? null
        : costed.reduce((sum, line) => sum + (line.purchaseCost ?? 0) * lineCount(line), 0),
  };
}

/** A design offered to add with one tap: it came in the supplier's last delivery. */
export interface DesignFromBefore {
  itemId: number;
  name: string;
  code: string;
}

/**
 * The designs of the supplier's last delivery (`GET /api/goods-receipt/{id}`) that this delivery
 * does not hold yet, once each, in the order they came — the phone's "Last time from Raj Textiles"
 * (canvas, board E1).
 */
export function designsFromBefore(
  last: GoodsReceipt | null | undefined,
  draft: ReceiveDraft,
  most = 6,
): DesignFromBefore[] {
  const held = new Set(draft.lines.map((line) => line.itemId));
  const seen = new Set<number>();
  const designs: DesignFromBefore[] = [];

  for (const line of last?.lines ?? []) {
    if (held.has(line.itemId) || seen.has(line.itemId)) {
      continue;
    }

    seen.add(line.itemId);
    designs.push({ itemId: line.itemId, name: line.itemName, code: line.itemCode });
  }

  return designs.slice(0, most);
}

/** "Book in 6 garments". */
export function receiveLabel(count: number): string {
  return count === 0 ? 'Book in' : `Book in ${count} garment${count === 1 ? '' : 's'}`;
}

/**
 * Why the delivery cannot be received yet, in the words of this screen — "Choose a colour for Gold
 * Zari Sherwani", not "Line 3: choose colour". `names` names a design by its id.
 */
export function receiveProblems(
  draft: ReceiveDraft,
  axesByItem: Record<number, ItemVariantAxisOptions[] | undefined>,
  names: (itemId: number) => string,
): string[] {
  const problems: string[] = [];
  const named = draft.lines.filter(
    (line): line is ReceiveLine & { itemId: number } => line.itemId !== null,
  );

  if (named.length === 0) {
    return ['Choose the design that came in.'];
  }

  for (const line of named) {
    const name = names(line.itemId);
    const axes = axesByItem[line.itemId];

    if (axes === undefined) {
      problems.push(`Reading the sizes of ${name}…`);
      continue;
    }

    if (axes.length === 0) {
      problems.push(
        `${name}’s category has nothing that tells its garments apart yet, so it cannot take stock.`,
      );
      continue;
    }

    // Nothing counted yet asks for the open look; otherwise every look booked in must be whole.
    const looks = countedLooks(line);
    const missing = splitAxes(axes).looks.filter((axis) =>
      (looks.length === 0 ? [line] : looks).some(
        (entry) => entry.look[axis.attributeId] === undefined,
      ),
    );

    if (missing.length > 0) {
      problems.push(
        `Choose ${missing.map((axis) => axis.attributeName.toLowerCase()).join(' and ')} for ${name}.`,
      );
    }

    if (lineCount(line) === 0) {
      problems.push(`Count at least one garment of ${name}.`);
    }

    if (line.purchaseCost !== null && line.purchaseCost < 0) {
      problems.push('A cost cannot be negative.');
    }

    if ((line.rentForEvent ?? 0) < 0 || (line.extraDayRent ?? 0) < 0) {
      problems.push('A rent cannot be negative.');
    }

    if (line.extraDayRent !== null && line.rentForEvent === null) {
      problems.push(`Give the rent for the event of ${name} before an extra day.`);
    }
  }

  return problems;
}

/**
 * One garment of the delivery, for step 2 — the list where each gets a photo, shows the code it will
 * be given and takes the shop's own number (canvas "Garment numbers flow", direction B).
 *
 * <p>
 * In exactly the order {@link toIntakeDraft} sends them: `lineIndex` is the intake line (so the
 * preview's and the result's lines), `position` the garment within it.
 * </p>
 */
export interface ReceiveGarment {
  /** Stable while the counts do not change: the design line, the size and which one of it. */
  key: string;
  /** The design line it came from ({@link ReceiveLine.key}) — its prices. */
  lineKey: string;
  itemId: number;
  /** Its colour (and any other look axis): attributeId → valueId. */
  look: Record<number, number>;
  /** "40", or "One size". */
  sizeLabel: string;
  lineIndex: number;
  position: number;
}

/** A garment's key: its line, look, size and which one of them. */
function garmentKey(line: ReceiveLine, entry: ReceiveLook, size: string, position: number): string {
  return `${line.key}|${lookKey(entry.look)}|${size}|${position}`;
}

export function receiveGarments(
  draft: ReceiveDraft,
  axesByItem: Record<number, ItemVariantAxisOptions[] | undefined>,
): ReceiveGarment[] {
  const garments: ReceiveGarment[] = [];
  let lineIndex = 0;

  for (const line of draft.lines) {
    if (line.itemId === null) {
      continue;
    }

    for (const entry of countedLooks(line)) {
      for (const size of sizeChoices(axesByItem[line.itemId] ?? [])) {
        const quantity = entry.counts[size.key] ?? 0;

        if (quantity === 0) {
          continue;
        }

        for (let position = 0; position < quantity; position += 1) {
          garments.push({
            key: garmentKey(line, entry, size.key, position),
            lineKey: line.key,
            itemId: line.itemId,
            look: entry.look,
            sizeLabel: size.label,
            lineIndex,
            position,
          });
        }

        lineIndex += 1;
      }
    }
  }

  return garments;
}

/**
 * "Fill from 21": every garment, in list order, numbered on from `start` — keyed as
 * {@link receiveGarments} keys them.
 */
export function fillReferences(
  garments: readonly ReceiveGarment[],
  start: number,
): Record<string, string> {
  const numbers = referencesFrom(start, garments.length);

  return Object.fromEntries(garments.map((garment, index) => [garment.key, numbers[index]!]));
}

/**
 * The delivery as the website's intake draft — one line per look and size with a count, the colour
 * and the size together as its axes — ready for `toBatchRequest`. `references` are the shop's own numbers
 * typed in step 2, keyed by {@link ReceiveGarment.key}.
 */
export function toIntakeDraft(
  draft: ReceiveDraft,
  axesByItem: Record<number, ItemVariantAxisOptions[] | undefined>,
  references: Record<string, string> = {},
): IntakeDraft {
  const lines: IntakeLine[] = [];

  for (const line of draft.lines) {
    if (line.itemId === null) {
      continue;
    }

    for (const entry of countedLooks(line)) {
      for (const size of sizeChoices(axesByItem[line.itemId] ?? [])) {
        const quantity = entry.counts[size.key] ?? 0;

        if (quantity > 0) {
          lines.push({
            ...emptyLine(),
            itemId: line.itemId,
            axisValues: { ...entry.look, ...size.values },
            quantity,
            condition: line.condition,
            purchaseCost: line.purchaseCost,
            rentForEvent: line.rentForEvent,
            extraDayRent: line.extraDayRent,
            referenceNumbers: Array.from(
              { length: quantity },
              (_, position) => references[garmentKey(line, entry, size.key, position)] ?? null,
            ),
          });
        }
      }
    }
  }

  return {
    purchasedOn: draft.receivedOn,
    supplierVendorId: draft.supplierVendorId,
    supplierReference: '',
    notes: draft.note,
    lines,
  };
}
